import {
    Component,
    Input,
    ViewChild,
    ViewChildren,
    ElementRef,
    ViewEncapsulation,
    Inject,
    ChangeDetectorRef,
    PLATFORM_ID,
    QueryList,
    AfterViewInit,
    Renderer2,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { SpecialModule } from 'lib/modules/special.module';
import { CmsPartialComponentClass } from 'lib/components/cms/cms-partial-component.class';
import { BootstrapMarketoService } from 'lib/services/bootstrap/bootstrap-marketo.service';
import { ExxComError } from 'lib/classes/exxcom-error.class';
import { state, trigger, animate, style, transition } from '@angular/animations';

const scriptName = 'exx-accordion-image-and-text';

export interface ExxAccordionInstance {
    button_link: {
        title: string;
        href: string;
        routerLink: string;
    };
    image: {
        href: string;
        webp: string;
        routerLink: string;
        title: string;
    };
    subheader: string;
    header: string;
    body_copy: HTMLInputElement;
    instance_background_color: string;
}

@Component({
    selector: 'exx-accordion-image-and-text',
    templateUrl: './accordion-image-and-text.component.html',
    styleUrls: ['./accordion-image-and-text.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [
        trigger('imgChange', [
            state('hidden', style({ opacity: 0 })),
            state('visible', style({ opacity: 1 })),
            transition('hidden => visible', [animate('500ms')]),
            transition('visible => hidden', [animate(0)]),
        ]),
        trigger('colorChange', [
            state('currentColor', style({ background: '{{backgroundColor}}' }), { params: { backgroundColor: 'none' } }),
            state('nextColor', style({ background: '{{nextBackgroundColor}}' }), { params: { nextBackgroundColor: 'none' } }),
            transition('currentColor => nextColor', [animate('500ms')]),
            transition('nextColor => currentColor', [animate(0)]),
        ]),
        trigger('accordionChange', [
            state('chevron_down', style({ transform: 'rotate(0deg)' })),
            state('chevron_up', style({ transform: 'rotate(180deg)' })),
            transition('chevron_down => chevron_up', [animate('250ms ease-out')]),
            transition('chevron_up => chevron_down', [animate('150ms ease-in')]),
        ]),
    ],
    standalone: true,
    imports: [CommonModule, SpecialModule],
})
export class ExxAccordionImageAndTextComponent extends CmsPartialComponentClass implements AfterViewInit {
    // Contentstack specific requirement
    // can be set to anything if using as internal component
    @Input() uniqueIndex: number;

    // Marketo Form Buttons
    @Input() dataUrl: string;
    @Input() dataTitle: string;
    @Input() solutionID: string;

    @Input() imageOnRight: boolean = true;
    @Input() mainImage: string = '';
    @Input() mainSubtitle: string = '';
    @Input() mainTitle: string = '';
    @Input() mainBody: HTMLInputElement = null;
    @Input() useCustomBackgroundColor: boolean;
    @Input() predefinedBackgroundColor: string;
    @Input() backgroundColor: string;
    @Input() instanceBackgroundColorToggle: boolean = false;
    @Input() instances: ExxAccordionInstance[] = [];

    @Input() mainButtonLinkTitle: string = '';
    @Input() mainButtonLink: string = '';
    @Input() mainButtonMarketoFormSelection: string = '';
    @Input() mainButtonMarketoFormID: number = null;

    @ViewChild('accordionImage') accordionImage: ElementRef;
    @ViewChild('desktopAccordionImage') desktopAccordionImage: ElementRef<HTMLImageElement>;
    @ViewChildren('accordionItem') accordionItems: QueryList<ElementRef>;

    currentAccordion = -1;
    currentImage: string = '';
    newCurrentImage: string = '';
    currentImageSize: string = '456px';

    imgState = 'hidden';
    subImgState = 'visible';
    accordionState = 'hidden';

    colorChangeState = 'currentColor';
    currentBackgroundColor: string = '';
    nextBackgroundColor: string = '';

    isBrowser: boolean;
    isInitialized = false;

    constructor(
        public bootstrapMarketoService: BootstrapMarketoService,
        @Inject(PLATFORM_ID) private platformId: Object,
        private cdr: ChangeDetectorRef,
        private renderer: Renderer2
    ) {
        super({
            dependencies: {
                bootstrapMarketoService,
            },
        });
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    ngOnInit() {
        this.currentImage = this.mainImage;
        if (!this.predefinedBackgroundColor) this.predefinedBackgroundColor = this.backgroundColor;
        if (this.useCustomBackgroundColor) {
            this.currentBackgroundColor = this.backgroundColor;
        } else {
            this.currentBackgroundColor = this.predefinedBackgroundColor;
            this.backgroundColor = this.predefinedBackgroundColor;
        }

        // Set first item open before first render to avoid body-fade-out on @defer init
        if (this.mainTitle === '' && !this.mainBody) {
            this.currentAccordion = 0;
        }
    }

    ngAfterViewInit() {
        if (!this.isBrowser) {
            return;
        }
        this.resetAccordionHeights();
        this.isInitialized = true;
        this.cdr.markForCheck();
    }

    getWebpImg: (src: string) => string;

    // The accordion image get sets within imageAnimate()
    // this prevents issues with images loading in before an animation finishes
    async setAccordion(index: number) {
        try {
            this.accordionAnimate(index);
            const newImage = this.currentAccordion == -1 ? this.mainImage : this.instances[index]?.image?.href;
            if (!this.instanceBackgroundColorToggle) {
                this.nextBackgroundColor = this.currentAccordion == -1 ? this.backgroundColor : this.instances[index]?.instance_background_color;
            } else {
                this.nextBackgroundColor = this.backgroundColor;
            }
            this.imageAnimate(newImage == '' ? this.mainImage : newImage);
        } catch (err) {
            console.error(...new ExxComError(923141, scriptName, err).stamp());
        }
    }

    accordionAnimate(newIndex: number) {
        this.closeAccordionAnimate(newIndex);
        this.openAccordionAnimate(newIndex);
    }

    closeAccordionAnimate(_newIndex: number) {
        if (!this.isBrowser || this.currentAccordion < 0) {
            return;
        }

        const currentItemElement = this.getAccordionItemByIndex(this.currentAccordion);
        if (!currentItemElement) {
            return;
        }

        const headerElement = currentItemElement.querySelector('.accordion-item-title') as HTMLElement;
        if (headerElement) {
            const newHeight = headerElement.offsetHeight + 32;
            this.renderer.setStyle(currentItemElement, 'height', `${newHeight}px`);
        }
    }

    private getAccordionItemByIndex(index: number): HTMLElement | null {
        if (!this.accordionItems || index < 0) {
            return null;
        }
        const items = this.accordionItems.toArray();
        return items[index]?.nativeElement || null;
    }

    // This method also handles currentAccordion index changes
    openAccordionAnimate(newIndex: number, isInit = false) {
        if (!this.isBrowser) {
            return;
        }

        const closing = !isInit && (this.currentAccordion === newIndex);
        const nextItemElement = this.getAccordionItemByIndex(newIndex);

        if (!nextItemElement) {
            return;
        }

        // Only update state when called from user interaction, not from height init
        if (!isInit) {
            this.currentAccordion = newIndex === this.currentAccordion ? -1 : newIndex;
            this.cdr.markForCheck();
        }

        // Get child elements scoped to the specific accordion item
        const headerElement = nextItemElement.querySelector('.accordion-item-title') as HTMLElement;
        const subheaderElement = nextItemElement.querySelector('.accordion-item-label') as HTMLElement;
        const bodyElement = nextItemElement.querySelector('.accordion-item-copy') as HTMLElement;
        const tabletImages = nextItemElement.querySelectorAll<HTMLImageElement>('.accordion-image-tablet');

        let nextInstanceHeight = 32; // 32 is for default padding

        if (headerElement) {
            nextInstanceHeight += headerElement.offsetHeight;
        }

        if (!closing) {
            if (subheaderElement) {
                nextInstanceHeight += subheaderElement.scrollHeight + 4; // 4 accounts for subheader margin
            }

            if (bodyElement) {
                nextInstanceHeight += bodyElement.offsetHeight + 32; // 32 accounts for body margins
            }

            // Handle image height - check if image is loaded (iOS lazy loading fix)
            const targetImage = tabletImages.length > 0 ? tabletImages[tabletImages.length - 1] : null;
            if (targetImage) {
                if (targetImage.complete && targetImage.offsetHeight > 0) {
                    // Image already loaded, add its height
                    nextInstanceHeight += targetImage.offsetHeight + 24; // 24 accounts for top img margin
                } else {
                    // Image not loaded yet - add listener to recalculate height when loaded
                    const currentHeight = nextInstanceHeight;

                    const unlisten = this.renderer.listen(targetImage, 'load', () => {
                        if (targetImage.offsetHeight > 0) {
                            const newHeight = currentHeight + targetImage.offsetHeight + 24;
                            this.renderer.setStyle(nextItemElement, 'height', `${newHeight}px`);
                            this.cdr.markForCheck();
                        }
                        unlisten();
                    });
                }
            }
        }

        this.renderer.setStyle(nextItemElement, 'height', `${nextInstanceHeight}px`);
    }

    resetAccordionHeights() {
        if (!this.isBrowser || !this.accordionItems) {
            return;
        }

        this.accordionItems.forEach((itemRef: ElementRef) => {
            const element = itemRef.nativeElement;
            const headerElement = element.querySelector('.accordion-item-title') as HTMLElement;

            if (headerElement) {
                const height = headerElement.offsetHeight + 32;
                this.renderer.setStyle(element, 'height', `${height}px`);
            }
        });

        if (this.currentAccordion !== -1) {
            this.openAccordionAnimate(this.currentAccordion, true);
        }
    }

    imageAnimate(newImage: string) {
        if (!this.isBrowser) {
            return;
        }

        // The following code accounts for the in-between viewports from desktop to tablet.
        // Because we use two image elements to accomplish the overlay fade in effect
        //  and one of the images has absolute positioning
        //  that particular image does not scale to its parent container.
        // Thus we apply its new max-width manually here.
        if (this.desktopAccordionImage?.nativeElement) {
            this.currentImageSize = this.desktopAccordionImage.nativeElement.offsetWidth + 'px';
        }

        if (this.currentImage !== newImage) {
            this.subImgState = 'hidden';
        }
        this.imgState = 'visible';
        this.newCurrentImage = newImage;
        this.colorChangeState = 'nextColor';
        this.cdr.markForCheck();

        setTimeout(() => {
            this.currentImage = newImage;
            this.subImgState = 'visible';
            this.imgState = 'hidden';
            this.currentBackgroundColor = this.nextBackgroundColor;
            this.colorChangeState = 'currentColor';
            this.cdr.markForCheck();
        }, 500);
    }
}
